import { devMode, env, getHost, version } from "@src/env";

import { StreamProxy } from "@src/services/streamProxy";

import { SharedPromise } from "@src/utils/SharedPromise";
import { getTimestampInSeconds } from "@src/utils/getTimestampInSeconds";

import type {
  AudioLinksResponse,
  ElectronVersionResponse,
  FrontendVersionResponse,
  HeadersResponse,
} from "./types";

export class Api {
  private static shared = new SharedPromise();

  static async getAudioLinks(idRadio: string): Promise<string[]> {
    const key = `getAudioLinks:${idRadio}`;

    return Api.shared.execute(key, () => {
      return new Promise((resolve, reject) => {
        const host = getHost();
        const url = `${host}/api/audio-links/${idRadio}`;
        fetch(url)
          .then((response) => response.json())
          .then((response: AudioLinksResponse) => {
            resolve(response.links);
          })
          .catch((error) => {
            console.log("[getAudioLinks] error:", error);
            reject(error);
          });
      });
    });
  }

  static async getFrontendVersion(): Promise<string> {
    const key = "getFrontendVersion";

    return Api.shared.execute(key, () => {
      return new Promise((resolve) => {
        // Development
        if (devMode) {
          resolve(version);
          return;
        }

        // Production
        const cacheBuster = getTimestampInSeconds();
        const url = `${env.api}/frontend-version?cache_buster=${cacheBuster}`;
        fetch(url)
          .then((response) => response.json())
          .then(({ success, response }: FrontendVersionResponse) => {
            if (success) {
              resolve(response.version || version);
            } else {
              resolve(version); // fallback
            }
          })
          .catch((error) => {
            console.log("[getFrontendVersion] error:", error);
            resolve(version); // fallback
          });
      });
    });
  }

  static async getHeaders(fromUrl: string): Promise<Record<string, string>> {
    const cleanUrl = StreamProxy.removeProxy(fromUrl);
    const key = `getHeaders:${cleanUrl}`;

    return Api.shared.execute(key, () => {
      return new Promise((resolve, reject) => {
        const url = `${env.api}/headers?url=${encodeURIComponent(cleanUrl)}`;
        fetch(url)
          .then((response) => response.text())
          .then((response) => {
            try {
              return JSON.parse(response);
            } catch {
              return {
                success: false,
                response: `Invalid JSON returned: ${response}`,
              } satisfies HeadersResponse;
            }
          })
          .then(({ success, response }: HeadersResponse) => {
            if (success) {
              resolve(response.headers);
            } else {
              console.log("[getHeaders] parsing error:", response);
              reject(response);
            }
          })
          .catch((error) => {
            console.log("[getHeaders] http error:", error);
            reject(error);
          });
      });
    });
  }

  static async getElectronVersion(): Promise<ElectronVersionResponse> {
    const key = "getElectronVersion";

    return Api.shared.execute(key, async () => {
      const cacheBuster = getTimestampInSeconds();
      const url = `${env.api}/electron-version?cb=${cacheBuster}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.statusText}`);
      }
      return response.json();
    });
  }
}
